import { Button, InputErrorMessage, Otp } from "@cynthai/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { z } from "zod";

import {
  apiErrorData,
  extractApiExceptionCodeFromData,
} from "@/core/lib/api-errors";
import { jwtVerifyMutation } from "@/openapi/@tanstack/react-query.gen";

const verifySchema = z.object({ code: z.coerce.string().optional() });

export const Route = createFileRoute("/auth/verify")({
  component: Verify,
  validateSearch: (search) => verifySchema.parse(search),
});

function Verify() {
  const { code: defaultCode } = Route.useSearch();
  const navigate = useNavigate();
  const otpRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate: verify, isPending } = useMutation({
    ...jwtVerifyMutation(),
    retry: 0,
    onSuccess: () => {
      void navigate({ to: "/" });
    },
    onError: (err) => {
      const code = extractApiExceptionCodeFromData(apiErrorData(err) ?? null);
      if (code === "VERIFY_USER_BAD_TOKEN") {
        setErrorMessage("Wrong code");
      } else if (code === "VERIFY_USER_ALREADY_VERIFIED") {
        void navigate({ to: "/" });
        setErrorMessage("User already verified");
      } else if (code !== "TOO_MANY_ATTEMPTS") {
        // TOO_MANY_ATTEMPTS is surfaced by the global response interceptor toast
        setErrorMessage("Unknwon server error");
      }
    },
  });

  const handleComplete = useCallback(
    (value?: string) => {
      if (value) {
        verify({ body: { token: value } });
      }
    },
    [verify],
  );

  return (
    <>
      <p className="text-text-primary mb-2 text-2xl font-semibold">
        Verify your account
      </p>

      <p className="text-text-secondary mb-9">
        Please type the code that was provided for you in the email and press{" "}
        {'"Verify"'}.
      </p>
      <div className="flex justify-between">
        <Otp
          ref={otpRef}
          autoFocus
          onComplete={handleComplete}
          defaultValue={defaultCode ?? ""}
          errorMessage={null}
          isInvalid={errorMessage !== null}
        />
      </div>
      <div className="my-2 mt-10 flex flex-col items-stretch gap-4">
        {errorMessage && <InputErrorMessage>{errorMessage}</InputErrorMessage>}
        <Button
          color="primary"
          size="lg"
          isLoading={isPending}
          onPress={() => {
            if (otpRef.current) {
              handleComplete(otpRef.current.value);
            }
          }}
        >
          Verify account
        </Button>
      </div>
    </>
  );
}
